import type { ElementType, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { AppLayout } from '@/components/AppLayout';
import { HIRE_PAGE_CONTAINER, HIRE_SECTION_SPACING } from '@/lib/hireResponsive';
import { Card } from '@/components/ui/card';
import { StageBadge } from '@/components/StageBadge';
import { DualScoreBadge } from '@/components/DualScoreBadge';
import { ScoreBadge } from '@/components/ScoreBadge';
import SarSymbol from '@/components/SarSymbol';
import AedSymbol from '@/components/AedSymbol';
import {
  FileText, Video, Users, Briefcase, LayoutDashboard, BarChart3,
  UserSearch, Settings, ShieldCheck, Bell, Crown, UserRound, Send,
  XCircle, Clock, Play, Pause, Share2, History, RotateCcw, ArrowRight,
  MapPin, CalendarPlus, UserCheck, Download, Sparkles, Star, Lock,
  Mail, BookOpen, ToggleLeft,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import forkGreenLogo from '@/assets/fork-green.svg';
import { Separator } from '@/components/ui/separator';

type TocItem = { id: string; label: string };
type IconName = keyof typeof guideIcons;
type IconItem = { icon: IconName; label: string; description: string; color?: string };
type BadgeItem = { label: string; description: string; stage?: string; className?: string };
type PillItem = { label: string; description: string; className: string };
type SourceItem = { label: string; description: string; badgeLabel: string; className: string; logo?: boolean };
type InfoItem = { title: string; body: string };
type TextItem = { title: string; description: string };
type FieldItem = { field: string; description: string };
type ScoreTier = { range: string; label: string; description: string; className: string; dotClassName: string; textClassName: string };
type TableRow = { format?: string; example?: string; usedFor?: string; role?: string; level?: number; description: string };
type GuideSection = { title: string; description?: string; [key: string]: unknown };
type GuideCopy = { title: string; description: string; contents: string; toc: TocItem[]; sections: Record<string, GuideSection> };

const guideIcons = {
  LayoutDashboard,
  Briefcase,
  Users,
  Video,
  UserSearch,
  Send,
  BarChart3,
  ShieldCheck,
  Settings,
  Bell,
  UserRound,
  Lock,
  BookOpen,
  Share2,
  FileText,
  Mail,
  XCircle,
  UserCheck,
  ToggleLeft,
  CalendarPlus,
  Crown,
  MapPin,
  Clock,
  Star,
  ArrowRight,
  RotateCcw,
  Sparkles,
  Download,
  Play,
  Pause,
  History,
} as const;

const idSamples = [
  { format: 'JOB-XXXX', example: 'JOB-0042', className: 'text-primary' },
  { format: 'FH-XXXXX', example: 'FH-00127', className: 'text-primary' },
  { format: 'SK-XXXXX', example: 'SK-00089', className: 'text-success' },
  { format: 'INT-XXXXX', example: 'INT-00015', className: 'text-primary' },
] as const;

function Section({ title, id, children }: { title: string; id?: string; children: ReactNode }) {
  return (
    <div id={id} className="scroll-mt-20">
      <h2 className="text-lg font-bold text-foreground mb-3">{title}</h2>
      {children}
    </div>
  );
}

function SubSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="mb-4">
      <h3 className="text-sm font-semibold text-foreground mb-2">{title}</h3>
      {children}
    </div>
  );
}

function BadgeRow({ label, children, description }: { label: string; children: ReactNode; description: string }) {
  return (
    <div className="flex items-start gap-4 py-2.5 border-b border-border/50 last:border-0">
      <div className="w-36 shrink-0 flex items-center">{children}</div>
      <div className="min-w-0">
        <p className="text-sm font-medium text-foreground">{label}</p>
        <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
      </div>
    </div>
  );
}

function IconRow({ icon: Icon, label, description, color }: { icon: ElementType; label: string; description: string; color?: string }) {
  return (
    <div className="flex items-start gap-3 py-2.5 border-b border-border/50 last:border-0">
      <div className={cn('w-8 h-8 rounded-lg flex items-center justify-center shrink-0', color || 'bg-muted')}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="min-w-0">
        <p className="text-sm font-medium text-foreground">{label}</p>
        <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
      </div>
    </div>
  );
}

function InfoBox({ items }: { items: InfoItem[] }) {
  if (items.length === 0) return null;

  return (
    <div className="mt-4 p-3 bg-muted/30 rounded-lg space-y-2">
      {items.map((item) => (
        <p key={item.title} className="text-xs text-muted-foreground">
          <strong>{item.title}:</strong> {item.body}
        </p>
      ))}
    </div>
  );
}

function asArray<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : [];
}

function iconRows(items: IconItem[]) {
  return items.map((item) => {
    const Icon = guideIcons[item.icon];
    return <IconRow key={item.label} icon={Icon} label={item.label} description={item.description} color={item.color} />;
  });
}

function stageRows(items: BadgeItem[]) {
  return items.map((item) => (
    <BadgeRow key={item.label} label={item.label} description={item.description}>
      <StageBadge stage={item.stage ?? item.label} className={item.className} />
    </BadgeRow>
  ));
}

function PillRow({ item }: { item: PillItem }) {
  return (
    <div className="flex items-start gap-3 py-2.5 border-b border-border/50 last:border-0">
      <span className={cn('inline-flex items-center justify-center px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider w-28 shrink-0', item.className)}>
        {item.label}
      </span>
      <p className="text-xs text-muted-foreground">{item.description}</p>
    </div>
  );
}

export default function UserGuide() {
  const { t } = useTranslation();
  const guide = t('hire.userGuide', { returnObjects: true }) as GuideCopy;
  const s = guide.sections;

  return (
    <AppLayout>
      <div className={`${HIRE_PAGE_CONTAINER} ${HIRE_SECTION_SPACING}`}>
        <Card className="shadow-card border-border border-t-4 border-t-primary overflow-hidden">
          <div className="p-5 sm:p-6">
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold font-display text-foreground">{guide.title}</h1>
            <p className="text-muted-foreground mt-1.5 text-sm">{guide.description}</p>
          </div>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-6">
          <Card className="p-4 h-fit lg:sticky lg:top-20 hidden lg:block">
            <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-3">{guide.contents}</p>
            <nav className="space-y-1">
              {guide.toc.map((item) => (
                <a key={item.id} href={`#${item.id}`} className="block text-xs text-muted-foreground hover:text-primary transition-colors py-1 px-2 rounded hover:bg-muted/50">
                  {item.label}
                </a>
              ))}
            </nav>
          </Card>

          <div className="space-y-8">
            <Card className="p-6">
              <Section title={s.pages.title} id="pages">
                <p className="text-xs text-muted-foreground mb-4">{s.pages.description as string}</p>
                <SubSection title={s.pages.mainTitle as string}><div className="space-y-0">{iconRows(asArray<IconItem>(s.pages.main))}</div></SubSection>
                <SubSection title={s.pages.utilityTitle as string}><div className="space-y-0">{iconRows(asArray<IconItem>(s.pages.utility))}</div></SubSection>
                <SubSection title={s.pages.detailTitle as string}><div className="space-y-0">{iconRows(asArray<IconItem>(s.pages.detail))}</div></SubSection>
                <SubSection title={s.pages.publicTitle as string}><div className="space-y-0">{iconRows(asArray<IconItem>(s.pages.public))}</div></SubSection>
              </Section>
            </Card>

            <Card className="p-6">
              <Section title={s.pipeline.title} id="pipeline">
                <p className="text-xs text-muted-foreground mb-4">{s.pipeline.description as string}</p>
                <div className="space-y-0">{stageRows(asArray<BadgeItem>(s.pipeline.stages))}</div>
                <Separator className="my-4" />
                <SubSection title={s.pipeline.otherTitle as string}>
                  <p className="text-xs text-muted-foreground mb-3">{s.pipeline.otherDescription as string}</p>
                  <div className="space-y-0">{stageRows(asArray<BadgeItem>(s.pipeline.other))}</div>
                </SubSection>
              </Section>
            </Card>

            <Card className="p-6">
              <Section title={s.automation.title} id="automation">
                <p className="text-xs text-muted-foreground mb-4">{s.automation.description as string}</p>
                <div className="space-y-3">
                  {asArray<TextItem>(s.automation.items).map((item) => (
                    <div key={item.title} className="p-4 border border-border/50 rounded-xl">
                      <div className="flex items-center gap-2 mb-2">
                        <ToggleLeft className="w-4 h-4 text-primary" />
                        <p className="text-sm font-semibold text-foreground">{item.title}</p>
                      </div>
                      <p className="text-xs text-muted-foreground">{item.description}</p>
                    </div>
                  ))}
                </div>
                <InfoBox items={asArray<InfoItem>(s.automation.info)} />
              </Section>
            </Card>

            <Card className="p-6">
              <Section title={s.communications.title} id="communications">
                <p className="text-xs text-muted-foreground mb-4">{s.communications.description as string}</p>
                <SubSection title={s.communications.channelsTitle as string}><div className="space-y-0">{iconRows(asArray<IconItem>(s.communications.channels))}</div></SubSection>
                <Separator className="my-4" />
                <SubSection title={s.communications.sentTitle as string}>
                  <div className="space-y-3">
                    {asArray<Record<'title' | 'trigger' | 'channels' | 'cta', string>>(s.communications.sent).map((item) => (
                      <div key={item.title} className="p-3 border border-border/50 rounded-lg">
                        <p className="text-sm font-semibold text-foreground">{item.title}</p>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-2">
                          <div><p className="text-[10px] font-bold uppercase text-muted-foreground">{s.communications.triggerLabel as string}</p><p className="text-xs text-muted-foreground">{item.trigger}</p></div>
                          <div><p className="text-[10px] font-bold uppercase text-muted-foreground">{s.communications.channelsLabel as string}</p><p className="text-xs text-muted-foreground">{item.channels}</p></div>
                          <div><p className="text-[10px] font-bold uppercase text-muted-foreground">{s.communications.ctaLabel as string}</p><p className="text-xs text-muted-foreground">{item.cta}</p></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </SubSection>
                <InfoBox items={asArray<InfoItem>(s.communications.info)} />
              </Section>
            </Card>

            <Card className="p-6">
              <Section title={s.emailTemplates.title} id="email-templates">
                <p className="text-xs text-muted-foreground mb-4">{s.emailTemplates.description as string}</p>
                <SubSection title={s.emailTemplates.typesTitle as string}><div className="space-y-0">{iconRows(asArray<IconItem>(s.emailTemplates.types))}</div></SubSection>
                <Separator className="my-4" />
                <SubSection title={s.emailTemplates.fieldsTitle as string}>
                  <p className="text-xs text-muted-foreground mb-3">{s.emailTemplates.fieldsDescription as string}</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {asArray<FieldItem>(s.emailTemplates.fields).map((item) => (
                      <div key={item.field} className="p-2 border border-border/40 rounded-lg flex items-start gap-2">
                        <span className="text-[10px] font-mono bg-muted px-1.5 py-0.5 rounded shrink-0">{'{{...}}'}</span>
                        <div>
                          <p className="text-xs font-medium text-foreground">{item.field}</p>
                          <p className="text-[10px] text-muted-foreground">{item.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </SubSection>
                <InfoBox items={asArray<InfoItem>(s.emailTemplates.info)} />
              </Section>
            </Card>

            <Card className="p-6">
              <Section title={s.jobStatuses.title} id="job-statuses">
                <p className="text-xs text-muted-foreground mb-4">{s.jobStatuses.description as string}</p>
                <div className="space-y-0">{stageRows(asArray<BadgeItem>(s.jobStatuses.items))}</div>
                <InfoBox items={asArray<InfoItem>(s.jobStatuses.info)} />
              </Section>
            </Card>

            <Card className="p-6">
              <Section title={s.candidateSources.title} id="candidate-sources">
                <p className="text-xs text-muted-foreground mb-4">{s.candidateSources.description as string}</p>
                <div className="space-y-0">
                  {asArray<SourceItem>(s.candidateSources.items).map((item) => (
                    <BadgeRow key={item.label} label={item.label} description={item.description}>
                      <span className={cn('inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border', item.className)}>
                        {item.logo && <img src={forkGreenLogo} alt="" className="w-3 h-3" />}
                        {item.badgeLabel}
                      </span>
                    </BadgeRow>
                  ))}
                </div>
                <Separator className="my-4" />
                <SubSection title={s.candidateSources.indicatorTitle as string}>
                  <div className="flex items-start gap-4 py-2.5">
                    <div className="w-36 shrink-0 flex items-center gap-2">
                      <img src={forkGreenLogo} alt={s.candidateSources.logoAlt as string} className="w-4 h-4" />
                      <span className="text-xs font-medium text-foreground">{s.candidateSources.logoLabel as string}</span>
                    </div>
                    <p className="text-xs text-muted-foreground">{s.candidateSources.logoDescription as string}</p>
                  </div>
                </SubSection>
              </Section>
            </Card>

            <Card className="p-6">
              <Section title={s.forkseek.title} id="forkseek">
                <p className="text-xs text-muted-foreground mb-4">{s.forkseek.description as string}</p>
                <SubSection title={s.forkseek.syncTitle as string}>
                  <div className="space-y-3">
                    {asArray<TextItem>(s.forkseek.syncItems).map((item) => (
                      <div key={item.title} className="p-3 border border-border/50 rounded-lg">
                        <p className="text-sm font-semibold text-foreground">{item.title}</p>
                        <p className="text-xs text-muted-foreground mt-1">{item.description}</p>
                      </div>
                    ))}
                  </div>
                </SubSection>
                <Separator className="my-4" />
                <SubSection title={s.forkseek.accountTitle as string}>
                  <p className="text-xs text-muted-foreground">{s.forkseek.accountDescription as string}</p>
                  <ul className="text-xs text-muted-foreground space-y-1.5 list-disc list-inside mt-2">
                    {asArray<string>(s.forkseek.accountItems).map((item) => <li key={item}>{item}</li>)}
                  </ul>
                </SubSection>
                <Separator className="my-4" />
                <SubSection title={s.forkseek.discoverabilityTitle as string}>
                  <p className="text-xs text-muted-foreground mb-2">{s.forkseek.discoverabilityDescription as string}</p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {asArray<Record<'mode' | 'description', string>>(s.forkseek.discoverabilityModes).map((item) => (
                      <div key={item.mode} className="p-2 border border-border/40 rounded-lg text-center">
                        <p className="text-xs font-semibold text-foreground">{item.mode}</p>
                        <p className="text-[10px] text-muted-foreground mt-0.5">{item.description}</p>
                      </div>
                    ))}
                  </div>
                </SubSection>
              </Section>
            </Card>

            <Card className="p-6">
              <Section title={s.aiScores.title} id="ai-scores">
                <p className="text-xs text-muted-foreground mb-4">{s.aiScores.description as string}</p>
                <SubSection title={s.aiScores.typesTitle as string}>
                  <div className="space-y-0">
                    {asArray<BadgeItem>(s.aiScores.types).map((item, index) => (
                      <BadgeRow key={item.label} label={item.label} description={item.description}>
                        {index === 0 && <ScoreBadge score={85} type="cv" size="sm" />}
                        {index === 1 && <ScoreBadge score={72} type="interview" size="sm" />}
                        {index === 2 && <DualScoreBadge cvScore={85} interviewScore={72} />}
                        {index === 3 && <DualScoreBadge cvScore={null} interviewScore={null} />}
                      </BadgeRow>
                    ))}
                  </div>
                </SubSection>
                <Separator className="my-4" />
                <SubSection title={s.aiScores.tiersTitle as string}>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {asArray<ScoreTier>(s.aiScores.tiers).map((tier) => (
                      <div key={tier.range} className={tier.className}>
                        <div className="flex items-center gap-2 mb-1">
                          <span className={cn('w-3 h-3 rounded-full', tier.dotClassName)} />
                          <span className={cn('text-sm font-bold', tier.textClassName)}>{tier.range}</span>
                        </div>
                        <p className={cn('text-xs', tier.textClassName)}>{tier.label} - {tier.description}</p>
                      </div>
                    ))}
                  </div>
                </SubSection>
                <InfoBox items={asArray<InfoItem>(s.aiScores.info)} />
              </Section>
            </Card>

            <Card className="p-6">
              <Section title={s.offerStatuses.title} id="offer-statuses">
                <p className="text-xs text-muted-foreground mb-4">{s.offerStatuses.description as string}</p>
                <div className="space-y-0">{asArray<PillItem>(s.offerStatuses.items).map((item) => <PillRow key={item.label} item={item} />)}</div>
                <InfoBox items={asArray<InfoItem>(s.offerStatuses.info)} />
              </Section>
            </Card>

            <Card className="p-6">
              <Section title={s.interviewStatuses.title} id="interview-statuses">
                <p className="text-xs text-muted-foreground mb-4">{s.interviewStatuses.description as string}</p>
                <div className="space-y-0">{stageRows(asArray<BadgeItem>(s.interviewStatuses.items))}</div>
                <InfoBox items={asArray<InfoItem>(s.interviewStatuses.info)} />
              </Section>
            </Card>

            <Card className="p-6">
              <Section title={s.calendar.title} id="calendar">
                <p className="text-xs text-muted-foreground mb-4">{s.calendar.description as string}</p>
                <div className="space-y-4 text-sm text-foreground/80">
                  <div>
                    <h4 className="font-semibold text-foreground mb-2 flex items-center gap-2"><CalendarPlus className="w-4 h-4 text-primary" />{s.calendar.supportedTitle as string}</h4>
                    <ul className="list-disc list-inside space-y-1 text-xs ms-2">{asArray<InfoItem>(s.calendar.supported).map((item) => <li key={item.title}><strong>{item.title}</strong> - {item.body}</li>)}</ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground mb-2">{s.calendar.whereTitle as string}</h4>
                    <ul className="list-disc list-inside space-y-1 text-xs ms-2">{asArray<InfoItem>(s.calendar.where).map((item) => <li key={item.title}><strong>{item.title}</strong> - {item.body}</li>)}</ul>
                  </div>
                  <div className="bg-muted/40 rounded-lg p-3"><p className="text-xs text-muted-foreground"><strong>{s.calendar.noteTitle as string}:</strong> {s.calendar.noteBody as string}</p></div>
                </div>
              </Section>
            </Card>

            <Card className="p-6">
              <Section title={s.invitations.title} id="invitations">
                <p className="text-xs text-muted-foreground mb-4">{s.invitations.description as string}</p>
                <SubSection title={s.invitations.lifecycleTitle as string}><div className="space-y-0">{asArray<PillItem>(s.invitations.items).map((item) => <PillRow key={item.label} item={item} />)}</div></SubSection>
                <InfoBox items={asArray<InfoItem>(s.invitations.info)} />
              </Section>
            </Card>

            <Card className="p-6">
              <Section title={s.idFormats.title} id="id-formats">
                <p className="text-xs text-muted-foreground mb-4">{s.idFormats.description as string}</p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b border-border"><th className="text-start py-2 px-3 text-xs font-semibold text-muted-foreground uppercase">{s.idFormats.formatHeader as string}</th><th className="text-start py-2 px-3 text-xs font-semibold text-muted-foreground uppercase">{s.idFormats.exampleHeader as string}</th><th className="text-start py-2 px-3 text-xs font-semibold text-muted-foreground uppercase">{s.idFormats.usedForHeader as string}</th></tr></thead>
                    <tbody>{asArray<TableRow>(s.idFormats.rows).map((row, index) => <tr key={idSamples[index]?.format ?? row.format} className="border-b border-border/50"><td className={cn('py-2 px-3 font-mono text-xs font-bold', idSamples[index]?.className)}>{idSamples[index]?.format ?? row.format}</td><td className="py-2 px-3 font-mono text-xs">{idSamples[index]?.example ?? row.example}</td><td className="py-2 px-3 text-xs text-muted-foreground">{row.usedFor}</td></tr>)}</tbody>
                  </table>
                </div>
                <InfoBox items={asArray<InfoItem>(s.idFormats.info)} />
              </Section>
            </Card>

            <Card className="p-6">
              <Section title={s.roles.title} id="roles">
                <p className="text-xs text-muted-foreground mb-4">{s.roles.description as string}</p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b border-border"><th className="text-start py-2 px-3 text-xs font-semibold text-muted-foreground uppercase">{s.roles.roleHeader as string}</th><th className="text-start py-2 px-3 text-xs font-semibold text-muted-foreground uppercase">{s.roles.levelHeader as string}</th><th className="text-start py-2 px-3 text-xs font-semibold text-muted-foreground uppercase">{s.roles.descriptionHeader as string}</th></tr></thead>
                    <tbody>{asArray<TableRow>(s.roles.rows).map((row) => <tr key={row.role} className="border-b border-border/50"><td className="py-2 px-3 text-xs font-semibold text-foreground">{row.role}</td><td className="py-2 px-3"><span className="text-[10px] font-mono bg-muted px-1.5 py-0.5 rounded">{row.level}</span></td><td className="py-2 px-3 text-xs text-muted-foreground">{row.description}</td></tr>)}</tbody>
                  </table>
                </div>
                <InfoBox items={asArray<InfoItem>(s.roles.info)} />
              </Section>
            </Card>

            <Card className="p-6">
              <Section title={s.icons.title} id="icons">
                <p className="text-xs text-muted-foreground mb-4">{s.icons.description as string}</p>
                <SubSection title={s.icons.candidateTitle as string}><div className="space-y-0">{iconRows(asArray<IconItem>(s.icons.candidateItems))}</div></SubSection>
                <SubSection title={s.icons.actionTitle as string}><div className="space-y-0">{iconRows(asArray<IconItem>(s.icons.actionItems))}</div></SubSection>
                <SubSection title={s.icons.currencyTitle as string}>
                  <div className="flex items-start gap-4 py-2.5 border-b border-border/50"><div className="w-36 shrink-0 flex items-center gap-2"><SarSymbol size={16} /><span className="text-xs font-medium text-foreground">SAR</span></div><p className="text-xs text-muted-foreground">{s.icons.sarDescription as string}</p></div>
                  <div className="flex items-start gap-4 py-2.5"><div className="w-36 shrink-0 flex items-center gap-2"><AedSymbol size={16} /><span className="text-xs font-medium text-foreground">AED</span></div><p className="text-xs text-muted-foreground">{s.icons.aedDescription as string}</p></div>
                </SubSection>
                <SubSection title={s.icons.notificationTitle as string}><div className="flex items-start gap-4 py-2.5"><div className="w-36 shrink-0 flex items-center gap-2"><span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold">5</span><span className="text-xs font-medium text-foreground">{s.icons.unreadLabel as string}</span></div><p className="text-xs text-muted-foreground">{s.icons.unreadDescription as string}</p></div></SubSection>
              </Section>
            </Card>

            <Card className="p-6">
              <Section title={s.actions.title} id="actions">
                <p className="text-xs text-muted-foreground mb-4">{s.actions.description as string}</p>
                <div className="space-y-3">{asArray<TextItem>(s.actions.items).map((item) => <div key={item.title} className="p-3 border border-border/50 rounded-lg"><p className="text-sm font-semibold text-foreground">{item.title}</p><p className="text-xs text-muted-foreground mt-1">{item.description}</p></div>)}</div>
              </Section>
            </Card>

            <Card className="p-6">
              <Section title={s.filters.title} id="filters">
                <p className="text-xs text-muted-foreground mb-4">{s.filters.description as string}</p>
                {asArray<{ title: string; items: InfoItem[] }>(s.filters.groups).map((group) => <SubSection key={group.title} title={group.title}><ul className="text-xs text-muted-foreground space-y-1.5 list-disc list-inside">{group.items.map((item) => <li key={item.title}><strong>{item.title}:</strong> {item.body}</li>)}</ul></SubSection>)}
              </Section>
            </Card>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
